import { Trans } from "@lingui/react/macro";
import { Box, Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import type { Locale } from "date-fns";
import { addDays, differenceInDays, format, isSameDay } from "date-fns";
import { useCallback, useMemo } from "react";
import type { DateRange } from "react-day-picker";
import DateRangeCalendar from "@/components/DateRangeCalendar";
import { useFeatureFlags } from "@/contexts/FeaturesContext";
import type { AccommodationDayInfo } from "@/types/api";
import { getAlternativeSx } from "./alternativeDatesSx";

interface AlternativeDatesAirbnbProps {
  dayList: Record<string, AccommodationDayInfo>;
  dateRange: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  locale: Locale;
}

function getAirbnbArrivalDays(dayList: Record<string, AccommodationDayInfo>): Date[] {
  const today = new Date();
  return Object.entries(dayList)
    .filter(([dateStr, info]) => {
      if (!info.open || info.stock === 0 || !info.can_arrive) return false;
      if (info.lead_days !== null && differenceInDays(new Date(dateStr), today) < info.lead_days)
        return false;
      // Only show arrivals that have at least one reachable departure
      return getAirbnbDepartureDays(dayList, new Date(dateStr)).length > 0;
    })
    .map(([dateStr]) => new Date(dateStr));
}

function getAirbnbDepartureDays(
  dayList: Record<string, AccommodationDayInfo>,
  arrivalDate: Date,
): Date[] {
  const arrivalStr = format(arrivalDate, "yyyy-MM-dd");
  const arrivalInfo = dayList[arrivalStr];
  if (!arrivalInfo) return [];

  const minNights = arrivalInfo.min_nights ?? 1;
  const maxNights = arrivalInfo.max_nights ?? 30;
  const valid: Date[] = [];

  for (let nights = minNights; nights <= maxNights; nights++) {
    // Check all intermediate days (arrival+1 through arrival+nights-1)
    let intermediateBlocked = false;
    for (let i = 1; i < nights; i++) {
      const midStr = format(addDays(arrivalDate, i), "yyyy-MM-dd");
      const midInfo = dayList[midStr];
      if (!midInfo || !midInfo.open || midInfo.stock === 0) {
        intermediateBlocked = true;
        break;
      }
    }
    // Once an intermediate day blocks, all longer stays are also impossible
    if (intermediateBlocked) break;

    const deptDate = addDays(arrivalDate, nights);
    const deptStr = format(deptDate, "yyyy-MM-dd");
    const deptInfo = dayList[deptStr];
    if (deptInfo?.open && deptInfo.stock > 0 && deptInfo.can_departure) {
      valid.push(deptDate);
    }
  }

  return valid;
}

export default function AlternativeDatesAirbnb({
  dayList,
  dateRange,
  onSelect,
  locale,
}: AlternativeDatesAirbnbProps) {
  const theme = useTheme();
  const { flags } = useFeatureFlags();
  const arrival = dateRange?.from;
  const departure = dateRange?.to;
  const step = !arrival ? "arrival" : !departure ? "departure" : "done";

  const validArrivalDays = useMemo(() => getAirbnbArrivalDays(dayList), [dayList]);
  const validDepartureDays = useMemo(
    () => (arrival ? getAirbnbDepartureDays(dayList, arrival) : []),
    [dayList, arrival],
  );

  const disabledDays = useMemo(() => {
    if (step === "arrival") {
      return [
        { before: new Date() },
        (date: Date) => !validArrivalDays.some((d) => isSameDay(d, date)),
      ];
    }
    // departure and done: only valid departure days for the selected arrival
    return [
      { before: new Date() },
      (date: Date) => !validDepartureDays.some((d) => isSameDay(d, date)),
    ];
  }, [step, validArrivalDays, validDepartureDays]);

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) {
        onSelect(undefined);
        return;
      }
      if (step === "arrival") {
        // Arrival selected — move to departure step
        onSelect({ from: range.from, to: undefined });
      } else {
        // departure or done: DayPicker gives { from: arrival, to: clickedDate }
        onSelect({ from: arrival!, to: range.to });
      }
    },
    [step, arrival, onSelect],
  );

  const handleCheckinClick = useCallback(() => {
    // Reset to arrival step
    onSelect(undefined);
  }, [onSelect]);

  const inputSx = (active: boolean) => ({
    flex: 1,
    p: 1.5,
    cursor: "pointer",
    borderBottom: 2,
    borderColor: active ? "primary.main" : "transparent",
    transition: "border-color 0.15s",
  });

  return (
    <>
      {/* Two-step input row */}
      <Stack
        direction="row"
        alignItems="center"
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Box sx={inputSx(step === "arrival")} onClick={handleCheckinClick}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
            <Trans>Check-in</Trans>
          </Typography>
          {arrival ? (
            <Typography variant="body2">
              {format(arrival, "EEE, d MMM yyyy", { locale })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">
              <Trans>Select date</Trans>
            </Typography>
          )}
        </Box>

        <Box sx={inputSx(step === "departure" || step === "done")}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
            <Trans>Check-out</Trans>
          </Typography>
          {departure ? (
            <Typography variant="body2">
              {format(departure, "EEE, d MMM yyyy", { locale })}
              {" · "}
              <Trans>{differenceInDays(departure, arrival!)} nights</Trans>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">
              <Trans>Select date</Trans>
            </Typography>
          )}
        </Box>
      </Stack>

      <Divider />

      {/* Calendar */}
      <DateRangeCalendar
        dateRange={step === "arrival" ? undefined : dateRange}
        onSelect={handleSelect}
        defaultMonth={arrival ?? new Date()}
        disabled={disabledDays}
        sx={getAlternativeSx("airbnb", theme, flags.disabledStrikethrough)}
      />

      {step === "done" && (
        <>
          <Divider />
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Button size="small" onClick={handleCheckinClick}>
              <Trans>Clear dates</Trans>
            </Button>
          </Box>
        </>
      )}
    </>
  );
}
